import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { Home, Lightbulb, Map, Briefcase, LifeBuoy, BookOpen, Sparkles, Wrench, Users, User, Settings, LogOut, Shield, ShieldCheck, TrendingUp, Rocket, Tag } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { LOGO_FULL, LOGO_ICON } from "@/components/common";
import { FullAccessBanner } from "@/components/FullAccessBanner";

const MOBILE = [["/", Home, "Home"], ["/ideas", Lightbulb, "Ideas"], ["/journey", Map, "Journey"], ["/business", Briefcase, "My Business"], ["/help", LifeBuoy, "Help"]];
const DESKTOP = [["/", Home, "Home"], ["/ideas", Lightbulb, "Ideas"], ["/journey", Map, "Journey"], ["/launch", Rocket, "Launch Plan"], ["/business", Briefcase, "My Business"],
  ["/resources", BookOpen, "Resources"], ["/coach", Sparkles, "AI Coach"], ["/tools", Wrench, "Tools"], ["/coaching", Users, "Get Coaching"], ["/plans", Tag, "My Plans"], ["/dashboard/challenge", Rocket, "Challenge 5 jours"], ["/sources", ShieldCheck, "Official Guidance"]];

const initials = (u) => `${u?.first_name?.[0] || ""}${u?.last_name?.[0] || ""}`.toUpperCase() || "S";

function Avatar({ user }) {
  return <span className="h-10 w-10 rounded-full bg-[#1E2B3C] text-white flex items-center justify-center text-sm font-bold font-display">{initials(user)}</span>;
}

function Sidebar() {
  const { user, logout, t } = useAuth();
  const item = ({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-white/10 text-white" : "text-white/60 hover:text-white hover:bg-white/5"}`;
  return (
    <aside className="hidden md:flex flex-col w-64 fixed inset-y-0 left-0 bg-[#1E2B3C] text-white p-5 z-40" data-testid="desktop-sidebar">
      <Link to="/" className="flex items-center gap-2 px-2 mb-8" data-testid="sidebar-logo">
        <img src={LOGO_ICON} alt="" className="h-9 w-9 rounded-xl" />
        <span className="font-display font-extrabold text-xl tracking-tight">sidebizz</span>
      </Link>
      <nav className="flex-1 space-y-1">
        {DESKTOP.map(([to, Icon, label]) => (
          <NavLink key={to} to={to} end={to === "/"} className={item} data-testid={`sidebar-nav-${label.toLowerCase().replace(/\s/g, "-")}`}>
            <Icon className="h-[18px] w-[18px]" />{t(label)}
          </NavLink>
        ))}
        {user?.role === "admin" && <NavLink to="/admin" end className={item} data-testid="sidebar-nav-admin"><Shield className="h-[18px] w-[18px]" />Super Admin</NavLink>}
        {user?.role === "admin" && <NavLink to="/admin/sales" className={item} data-testid="sidebar-nav-sales"><TrendingUp className="h-[18px] w-[18px]" />Sales</NavLink>}
        {user?.role === "admin" && <NavLink to="/admin/challenge" className={item} data-testid="sidebar-nav-admin-challenge"><Rocket className="h-[18px] w-[18px]" />Challenge admin</NavLink>}
      </nav>
      <div className="border-t border-white/10 pt-4 space-y-1">
        <NavLink to="/settings" className={item} data-testid="sidebar-nav-profile"><User className="h-[18px] w-[18px]" />{t("Profile")}</NavLink>
        <NavLink to="/settings#prefs" className={() => item({ isActive: false })} data-testid="sidebar-nav-settings"><Settings className="h-[18px] w-[18px]" />{t("Settings")}</NavLink>
        <button onClick={logout} className={item({ isActive: false }) + " w-full"} data-testid="sidebar-logout-btn"><LogOut className="h-[18px] w-[18px]" />{t("Logout")}</button>
        <div className="flex items-center gap-3 px-3 pt-3"><Avatar user={user} /><div className="min-w-0"><div className="text-sm font-semibold truncate">{user?.first_name} {user?.last_name}</div><div className="text-[11px] text-white/50">{user?.plan?.replace("_", " ")}</div></div></div>
      </div>
    </aside>
  );
}

function TopBar() {
  const { user, logout, t } = useAuth();
  const nav = useNavigate();
  return (
    <header className="md:hidden sticky top-0 z-40 bg-[#FBF8F2]/90 backdrop-blur-md pt-safe border-b border-[#EADEC9]/60">
      <div className="flex items-center justify-between px-4 h-14">
        <Link to="/" data-testid="topbar-logo"><img src={LOGO_FULL} alt="sidebizz" className="h-7 w-auto" /></Link>
        <DropdownMenu>
          <DropdownMenuTrigger data-testid="topbar-avatar-btn" className="rounded-full focus:outline-none"><Avatar user={user} /></DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52 rounded-xl">
            <DropdownMenuItem onClick={() => nav("/settings")} data-testid="menu-profile"><User className="h-4 w-4 mr-2" />{t("Profile")} & {t("Settings")}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav("/plans")} data-testid="menu-plans"><Tag className="h-4 w-4 mr-2" />My Plans</DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav("/checklist")} data-testid="menu-checklist">{t("Checklist")}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav("/sources")} data-testid="menu-official-guidance"><ShieldCheck className="h-4 w-4 mr-2" />Official Guidance</DropdownMenuItem>
            {user?.role === "admin" && <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin"><Shield className="h-4 w-4 mr-2" />Super Admin</DropdownMenuItem>}
            {user?.role === "admin" && <DropdownMenuItem onClick={() => nav("/admin/sales")} data-testid="menu-sales"><TrendingUp className="h-4 w-4 mr-2" />Sales</DropdownMenuItem>}
            {user?.role === "admin" && <DropdownMenuItem onClick={() => nav("/admin/challenge")} data-testid="menu-admin-challenge"><Rocket className="h-4 w-4 mr-2" />Challenge admin</DropdownMenuItem>}
            <DropdownMenuItem onClick={() => nav("/dashboard/challenge")} data-testid="menu-challenge"><Rocket className="h-4 w-4 mr-2" />Challenge 5 jours</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="menu-logout"><LogOut className="h-4 w-4 mr-2" />{t("Logout")}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

function BottomNav() {
  const { t } = useAuth();
  return (
    <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white/95 backdrop-blur-md border-t border-[#EADEC9] pb-safe" data-testid="mobile-bottom-nav">
      <div className="grid grid-cols-5 h-16">
        {MOBILE.map(([to, Icon, label]) => (
          <NavLink key={to} to={to} end={to === "/"} data-testid={`bottom-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) => `flex flex-col items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-wide transition-colors ${isActive ? "text-[#1DB99A]" : "text-[#8C9BA5]"}`}>
            {({ isActive }) => (<><span className={`flex h-8 w-12 items-center justify-center rounded-full transition-colors ${isActive ? "bg-[#E8F8F4]" : ""}`}><Icon className="h-5 w-5" /></span>{t(label)}</>)}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export default function AppLayout() {
  return (
    <div className="min-h-screen">
      <Sidebar />
      <TopBar />
      <main className="md:pl-64 pb-nav">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 md:px-10 pt-5 md:pt-10"><FullAccessBanner /><Outlet /></div>
      </main>
      <BottomNav />
    </div>
  );
}
